import { 
  Asset, 
  Liability, 
  NetWorthData, 
  Account, 
  AccountFormData, 
  AccountSnapshot, 
  SnapshotFormData,
  NetWorthHistory, 
  CategoryBreakdown, 
  ImportData,
  InsuranceReimbursement
} from '../types';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:5001/api';

// Helper function to get auth token
const getAuthToken = (): string | null => {
  return localStorage.getItem('authToken');
};

// Helper function to make authenticated requests
const authRequest = async (url: string, options: RequestInit = {}, returnRawResponse: boolean = false) => {
  const token = getAuthToken();
  
  // Don't set Content-Type for FormData (let browser set it with boundary)
  const isFormData = options.body instanceof FormData;
  const headers = {
    ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
    ...(token && { Authorization: `Bearer ${token}` }),
    ...options.headers,
  };

  const response = await fetch(`${API_BASE_URL}${url}`, {
    ...options,
    headers,
  });

  if (!response.ok) {
    const responseText = await response.text();
    let message = `HTTP error: ${response.status}`;
    try {
      const error = JSON.parse(responseText);
      message = error.error || error.message || message;
    } catch (_) {
      // response wasn't JSON, keep the default message
    }
    throw new Error(message);
  }

  if (returnRawResponse) {
    return response;
  }

  try {
    return await response.json();
  } catch (parseError) {
    throw new Error('Failed to parse response');
  }
};

// Authentication API
export const authAPI = {
  register: async (userData: { email: string; password: string; first_name?: string; last_name?: string }) => {
    return authRequest('/auth/register', {
      method: 'POST',
      body: JSON.stringify(userData),
    });
  },

  login: async (credentials: { email: string; password: string }) => {
    return authRequest('/auth/login', {
      method: 'POST',
      body: JSON.stringify(credentials),
    });
  },

  getProfile: async () => {
    return authRequest('/auth/profile');
  },

  updateProfile: async (profileData: { first_name?: string; last_name?: string }) => {
    return authRequest('/auth/profile', {
      method: 'PUT',
      body: JSON.stringify(profileData),
    });
  },

  googleAuth: async (idToken: string) => {
    return authRequest('/auth/google', {
      method: 'POST',
      body: JSON.stringify({ idToken }),
    });
  },
};

// Accounts API
export const accountsAPI = {
  getAccounts: async (filters?: { type?: 'asset' | 'liability'; account_type?: 'personal' | 'business' }) => {
    const params = new URLSearchParams();
    if (filters?.type) params.append('type', filters.type);
    if (filters?.account_type) params.append('account_type', filters.account_type);
    
    return authRequest(`/accounts?${params.toString()}`);
  },

  addAccount: async (accountData: {
    type: 'asset' | 'liability';
    account_type: 'personal' | 'business';
    institution: string;
    name: string;
    category: string;
    currency: string;
    notes?: string;
  }) => {
    return authRequest('/accounts', {
      method: 'POST',
      body: JSON.stringify(accountData),
    });
  },

  updateAccount: async (id: number, accountData: {
    type: 'asset' | 'liability';
    account_type: 'personal' | 'business';
    institution: string;
    name: string;
    category: string;
    currency: string;
    notes?: string;
  }) => {
    return authRequest(`/accounts/${id}`, {
      method: 'PUT',
      body: JSON.stringify(accountData),
    });
  },

  deleteAccount: async (id: number) => {
    return authRequest(`/accounts/${id}`, {
      method: 'DELETE',
    });
  },

  getAccountSnapshots: async (accountId: number) => {
    return authRequest(`/accounts/${accountId}/snapshots`);
  },

  addSnapshot: async (data: SnapshotFormData) => {
    return authRequest('/accounts/snapshots', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },
  deleteSnapshot: async (snapshotId: number) => {
    return authRequest(`/accounts/snapshots/${snapshotId}`, {
      method: 'DELETE',
    });
  },

  updateSnapshot: async (snapshotId: number, data: SnapshotFormData) => {
    // Since there's no update endpoint, we'll delete and recreate
    // This is handled in the component to ensure atomicity
    return { snapshotId, data };
  },

  // Plaid integration
  createLinkToken: async () => {
    return authRequest('/plaid/link-token', {
      method: 'POST',
    });
  },

  createUpdateLinkToken: async (connectionId: number) => {
    return authRequest(`/plaid/update-link-token/${connectionId}`, {
      method: 'POST',
    });
  },

  completeReauth: async (connectionId: number) => {
    return authRequest(`/plaid/connections/${connectionId}/complete-reauth`, {
      method: 'POST',
    });
  },

  getAccountMatchingSuggestions: async (data: {
    connection_id: number;
    selected_accounts: string[];
  }) => {
    return authRequest('/plaid/account-matching-suggestions', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  exchangeToken: async (data: { public_token: string; institution_name: string; selected_accounts?: string[] }) => {
    return authRequest('/plaid/exchange-token', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  importSelectedAccounts: async (data: {
    connection_id: number;
    selected_accounts: string[];
    account_mappings?: { [plaidAccountId: string]: number | null };
    account_type_overrides?: { [plaidAccountId: string]: 'business' };
    /** Per Plaid account: how far back to import on first sync */
    transaction_import?: { [plaidAccountId: string]: 'full' | 'after_existing' | 'from_link_date' };
  }) => {
    return authRequest('/plaid/import-accounts', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  getLinkedAccounts: async () => {
    return authRequest('/plaid/linked-accounts');
  },

  linkPlaidToManual: async (linkedAccountId: number, manualAccountId: number) => {
    return authRequest(`/plaid/accounts/${linkedAccountId}/link-manual`, {
      method: 'POST',
      body: JSON.stringify({ manual_account_id: manualAccountId }),
    });
  },

  unlinkPlaidAccount: async (linkedAccountId: number) => {
    return authRequest(`/plaid/accounts/${linkedAccountId}/unlink`, {
      method: 'POST',
    });
  },

  getAccountTransactions: async (linkedAccountId: number, startDate: string, endDate: string) => {
    return authRequest(`/plaid/accounts/${linkedAccountId}/transactions?start_date=${startDate}&end_date=${endDate}`);
  },

  syncAccounts: async () => {
    return authRequest('/plaid/sync', {
      method: 'POST',
    });
  },

  disconnectAccount: async (linkedAccountId: number) => {
    return authRequest(`/plaid/accounts/${linkedAccountId}`, {
      method: 'DELETE',
    });
  },

  getNetWorthHistory: async (days: number | 'all' = 30) => {
    return authRequest(`/accounts/history/networth?days=${days}`);
  },

  getCategoryBreakdown: async (type: 'asset' | 'liability') => {
    return authRequest(`/accounts/breakdown/category?type=${type}`);
  },

  importData: async (formData: FormData): Promise<any> => {
    const token = getAuthToken();
    
    const response = await fetch(`${API_BASE_URL}/accounts/import`, {
      method: 'POST',
      headers: {
        ...(token && { Authorization: `Bearer ${token}` }),
      },
      body: formData,
    });

    if (!response.ok) {
      const errorText = await response.text();
      let errorData;
      try {
        errorData = JSON.parse(errorText);
      } catch {
        errorData = { error: 'Network error' };
      }
      throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    return result;
  },
};

// Profile API
export const profileAPI = {
  getProfile: async () => {
    return authRequest('/profile');
  },

  updateProfile: async (profileData: {
    default_currency: string;
    theme: 'light' | 'dark';
    notifications_enabled: boolean;
    data_export_enabled: boolean;
  }) => {
    return authRequest('/profile', {
      method: 'PUT',
      body: JSON.stringify(profileData),
    });
  },

  changePassword: async (passwordData: {
    currentPassword: string;
    newPassword: string;
  }) => {
    return authRequest('/profile/password', {
      method: 'PUT',
      body: JSON.stringify(passwordData),
    });
  },

  exportData: async () => {
    return authRequest('/profile/export');
  },

  deleteAccount: async () => {
    return authRequest('/profile', {
      method: 'DELETE',
    });
  },
};

// Legacy API (for backward compatibility during migration)
export const legacyAPI = {
  getAssets: async () => {
    return authRequest('/networth/assets');
  },

  addAsset: async (assetData: { name: string; category: string; value: number; date: string; notes?: string }) => {
    return authRequest('/networth/assets', {
      method: 'POST',
      body: JSON.stringify(assetData),
    });
  },

  updateAsset: async (id: number, assetData: { name: string; category: string; value: number; date: string; notes?: string }) => {
    return authRequest(`/networth/assets/${id}`, {
      method: 'PUT',
      body: JSON.stringify(assetData),
    });
  },

  deleteAsset: async (id: number) => {
    return authRequest(`/networth/assets/${id}`, {
      method: 'DELETE',
    });
  },

  getLiabilities: async () => {
    return authRequest('/networth/liabilities');
  },

  addLiability: async (liabilityData: { name: string; category: string; amount: number; date: string; notes?: string }) => {
    return authRequest('/networth/liabilities', {
      method: 'POST',
      body: JSON.stringify(liabilityData),
    });
  },

  updateLiability: async (id: number, liabilityData: { name: string; category: string; amount: number; date: string; notes?: string }) => {
    return authRequest(`/networth/liabilities/${id}`, {
      method: 'PUT',
      body: JSON.stringify(liabilityData),
    });
  },

  deleteLiability: async (id: number) => {
    return authRequest(`/networth/liabilities/${id}`, {
      method: 'DELETE',
    });
  },

  getNetWorthSummary: async () => {
    return authRequest('/networth/summary');
  },
};

// Precious Metals API
export const preciousMetalsAPI = {
  getUserMetals: async () => {
    return authRequest('/precious-metals');
  },

  createMetal: async (data: {
    accountId: number;
    metalData: {
      metal_type: 'gold' | 'silver' | 'platinum' | 'palladium';
      weight: number;
      weight_unit: 'oz' | 'g';
      storage_location: 'home' | 'vault' | 'other';
      storage_details?: string;
    };
  }) => {
    return authRequest('/precious-metals', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  addTransaction: async (data: {
    metalId: number;
    transactionData: {
      transaction_type: 'buy' | 'sell' | 'add' | 'remove';
      quantity: number;
      price_per_unit?: number;
      total_value?: number;
      transaction_date: string;
      notes?: string;
    };
  }) => {
    return authRequest(`/precious-metals/${data.metalId}/transactions`, {
      method: 'POST',
      body: JSON.stringify({ transactionData: data.transactionData }),
    });
  },

  getMetalTransactions: async (metalId: number) => {
    return authRequest(`/precious-metals/${metalId}/transactions`);
  },

  getCurrentPrices: async () => {
    return authRequest('/precious-metals/prices');
  },

  // Get daily cached prices without authentication (for form calculations)
  getCurrentPricesPublic: async () => {
    try {
      console.log('Fetching daily metal prices via backend...');
      
      // Use our backend API which returns daily cached prices
      const response = await fetch('http://localhost:5001/api/precious-metals/prices', {
        headers: {
          'Accept': 'application/json',
        }
      });
      
      if (response.ok) {
        const prices = await response.json();
        console.log('Daily metal prices:', prices);
        return prices;
      } else {
        throw new Error(`Backend API failed: ${response.status}`);
      }
    } catch (error) {
      console.error('Error fetching metal prices from backend:', error);
      
      // Fallback to hardcoded prices if backend fails
      console.log('Using fallback hardcoded prices');
      const usdToCadRate = 1.35;
      return [
        {
          metal_type: 'gold',
          price_usd: 2300.00,
          price_cad: 2300.00 * usdToCadRate,
          date: new Date().toISOString().split('T')[0],
          source: `Estimated daily price (offline) - prices updated at 9:00 AM Eastern Time - $${(2300.00 * usdToCadRate).toFixed(2)} CAD/oz`
        },
        {
          metal_type: 'silver',
          price_usd: 27.50,
          price_cad: 27.50 * usdToCadRate,
          date: new Date().toISOString().split('T')[0],
          source: `Estimated daily price (offline) - prices updated at 9:00 AM Eastern Time - $${(27.50 * usdToCadRate).toFixed(2)} CAD/oz`
        },
        {
          metal_type: 'platinum',
          price_usd: 950.00,
          price_cad: 950.00 * usdToCadRate,
          date: new Date().toISOString().split('T')[0],
          source: `Estimated daily price (offline) - prices updated at 9:00 AM Eastern Time - $${(950.00 * usdToCadRate).toFixed(2)} CAD/oz`
        },
        {
          metal_type: 'palladium',
          price_usd: 1200.00,
          price_cad: 1200.00 * usdToCadRate,
          date: new Date().toISOString().split('T')[0],
          source: `Estimated daily price (offline) - prices updated at 9:00 AM Eastern Time - $${(1200.00 * usdToCadRate).toFixed(2)} CAD/oz`
        }
      ];
    }
  },

  getPriceHistory: async (metalType: string, days: number = 30) => {
    return authRequest(`/precious-metals/prices/${metalType}/history?days=${days}`);
  },

  updateMetal: async (metalId: number, data: {
    storage_location: 'home' | 'vault' | 'other';
    storage_details?: string;
  }) => {
    return authRequest(`/precious-metals/${metalId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  deleteMetal: async (metalId: number) => {
    return authRequest(`/precious-metals/${metalId}`, {
      method: 'DELETE',
    });
  },
};

export const cryptoAPI = {
  getUserCrypto: async () => {
    return authRequest('/crypto');
  },

  createCrypto: async (data: {
    accountId: number;
    coinType: string;
    quantity: number;
  }) => {
    return authRequest('/crypto', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  // Combined method to create crypto account and holding in one call
  createCryptoAccount: async (data: {
    account_type: 'personal' | 'business';
    category: string;
    name: string;
    currency: string;
    coinType: string;
    quantity: number;
    exchange?: string;
    walletAddress?: string;
    notes?: string;
  }) => {
    return authRequest('/crypto/holding', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  addTransaction: async (cryptoId: number, data: {
    transaction_type: 'buy' | 'sell';
    quantity: number;
    price_per_unit: number;
    transaction_date: string;
    notes?: string;
  }) => {
    return authRequest(`/crypto/${cryptoId}/transactions`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  getCryptoTransactions: async (cryptoId: number) => {
    return authRequest(`/crypto/${cryptoId}/transactions`);
  },

  getCoinPrices: async () => {
    try {
      console.log('Fetching daily crypto prices via backend...');
      
      const response = await fetch('http://localhost:5001/api/crypto/prices', {
        headers: {
          'Accept': 'application/json',
        }
      });
      
      if (response.ok) {
        const prices = await response.json();
        console.log('Daily crypto prices:', prices);
        return prices;
      } else {
        throw new Error(`Backend API failed: ${response.status}`);
      }
    } catch (error) {
      console.error('Error fetching crypto prices from backend:', error);
      
      // Fallback to hardcoded prices if backend fails
      console.log('Using fallback hardcoded crypto prices');
      const usdToCadRate = 1.35;
      return [
        {
          coin_type: 'bitcoin',
          price_usd: 45000.00,
          price_cad: 45000.00 * usdToCadRate,
          timestamp: new Date().toISOString(),
          source: `Estimated daily price (offline) - prices updated at 9:00 AM Eastern Time - $${(45000.00 * usdToCadRate).toFixed(2)} CAD`
        },
        {
          coin_type: 'ethereum',
          price_usd: 3000.00,
          price_cad: 3000.00 * usdToCadRate,
          timestamp: new Date().toISOString(),
          source: `Estimated daily price (offline) - prices updated at 9:00 AM Eastern Time - $${(3000.00 * usdToCadRate).toFixed(2)} CAD`
        }
      ];
    }
  },

  getPriceHistory: async (coinType: string, days: number = 30) => {
    return authRequest(`/crypto/prices/${coinType}/history?days=${days}`);
  },

  updateCrypto: async (cryptoId: number, data: {
    exchange: string;
    wallet_address?: string;
  }) => {
    return authRequest(`/crypto/${cryptoId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  deleteCrypto: async (cryptoId: number) => {
    return authRequest(`/crypto/${cryptoId}`, {
      method: 'DELETE',
    });
  },
};

// Transaction API
export const transactionAPI = {
  getCategories: async () => {
    const response = await fetch(`${API_BASE_URL}/transactions/categories`, {
      headers: { Authorization: `Bearer ${getAuthToken()}` },
    });
    if (!response.ok) throw new Error('Failed to get categories');
    return response.json();
  },
  
  createCategory: async (data: { name: string; color?: string; is_income?: boolean }) => {
    const response = await fetch(`${API_BASE_URL}/transactions/categories`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${getAuthToken()}`, 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    if (!response.ok) throw new Error('Failed to create category');
    return response.json();
  },

  updateCategory: async (id: number, data: { name: string; color: string; is_income?: boolean }) => {
    const response = await fetch(`${API_BASE_URL}/transactions/categories/${id}`, {
      method: 'PUT',
      headers: { Authorization: `Bearer ${getAuthToken()}`, 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    if (!response.ok) throw new Error('Failed to update category');
    return response.json();
  },

  getCategoryDeletionImpact: async (id: number) => {
    return authRequest(`/transactions/categories/${id}/deletion-impact`);
  },

  deleteCategory: async (id: number, reassignTo?: { categoryId?: number; subcategoryId?: number }) => {
    const response = await fetch(`${API_BASE_URL}/transactions/categories/${id}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${getAuthToken()}`,
      },
      body: JSON.stringify({
        reassign_to_category_id: reassignTo?.categoryId,
        reassign_to_subcategory_id: reassignTo?.subcategoryId,
      }),
    });
    if (!response.ok) throw new Error('Failed to delete category');
    return response.json();
  },

  bulkReassignTransactions: async (data: {
    transaction_ids: number[];
    new_category_id?: number;
    new_subcategory_id?: number;
  }) => {
    return authRequest('/transactions/bulk-reassign', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  bulkVerifyTransactions: async (data: { transaction_ids: number[]; is_verified: boolean }) => {
    return authRequest('/transactions/bulk-verify', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  populateDefaults: async () => {
    const response = await fetch(`${API_BASE_URL}/transactions/populate-defaults`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${getAuthToken()}` },
    });
    if (!response.ok) throw new Error('Failed to populate defaults');
    return response.json();
  },

  resetCategories: async () => {
    const response = await fetch(`${API_BASE_URL}/transactions/reset-categories`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${getAuthToken()}` },
    });
    if (!response.ok) throw new Error('Failed to reset categories');
    return response.json();
  },

  getDeletedCategories: async () => {
    const response = await fetch(`${API_BASE_URL}/transactions/categories/deleted`, {
      headers: { Authorization: `Bearer ${getAuthToken()}` },
    });
    if (!response.ok) throw new Error('Failed to get deleted categories');
    return response.json();
  },

  restoreCategory: async (id: number) => {
    const response = await fetch(`${API_BASE_URL}/transactions/categories/${id}/restore`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${getAuthToken()}` },
    });
    if (!response.ok) throw new Error('Failed to restore category');
    return response.json();
  },

  restoreSubcategory: async (subcategoryId: number, categoryId: number) => {
    const response = await fetch(`${API_BASE_URL}/transactions/subcategories/${subcategoryId}/restore/${categoryId}`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${getAuthToken()}` },
    });
    if (!response.ok) throw new Error('Failed to restore subcategory');
    return response.json();
  },

  getSubcategories: async (categoryId: number) => {
    const response = await fetch(`${API_BASE_URL}/transactions/categories/${categoryId}/subcategories`, {
      headers: { Authorization: `Bearer ${getAuthToken()}` },
    });
    if (!response.ok) throw new Error('Failed to get subcategories');
    return response.json();
  },
  
  createSubcategory: async (categoryId: number, data: { name: string }) => {
    const response = await fetch(`${API_BASE_URL}/transactions/categories/${categoryId}/subcategories`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${getAuthToken()}`, 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    if (!response.ok) throw new Error('Failed to create subcategory');
    return response.json();
  },

  updateSubcategory: async (id: number, data: { name: string }) => {
    return authRequest(`/transactions/subcategories/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  deleteSubcategory: async (id: number) => {
    return authRequest(`/transactions/subcategories/${id}`, {
      method: 'DELETE',
    });
  },

  getRules: async () => {
    const response = await fetch(`${API_BASE_URL}/transactions/rules`, {
      headers: { Authorization: `Bearer ${getAuthToken()}` },
    });
    if (!response.ok) throw new Error('Failed to get rules');
    return response.json();
  },

  getRuleById: async (id: number) => {
    const response = await fetch(`${API_BASE_URL}/transactions/rules/${id}`, {
      headers: { Authorization: `Bearer ${getAuthToken()}` },
    });
    if (!response.ok) throw new Error('Failed to get rule');
    return response.json();
  },
  
  createRule: async (data: { 
    rule_name: string; 
    rule_type: 'simple' | 'complex'; 
    category_id: number; 
    subcategory_id?: number | null; 
    confidence?: number;
    conditions?: Array<{
      field_type: 'merchant' | 'description' | 'amount' | 'account';
      operator: 'contains' | 'equals' | 'starts_with' | 'ends_with' | 'regex' | 'greater_than' | 'less_than';
      value: string;
      condition_order: number;
      logic_operator?: 'AND' | 'OR';
    }>;
    simple_patterns?: Array<{
      pattern_type: 'merchant' | 'description' | 'amount' | 'account';
      pattern: string;
    }>;
  }) => {
    const response = await fetch(`${API_BASE_URL}/transactions/rules`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${getAuthToken()}`, 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    if (!response.ok) throw new Error('Failed to create rule');
    return response.json();
  },

  previewRuleMatches: async (data: {
    rule_type: 'simple' | 'complex';
    conditions?: Array<{
      field_type: 'merchant' | 'description' | 'amount' | 'account';
      operator: 'contains' | 'equals' | 'starts_with' | 'ends_with' | 'regex' | 'greater_than' | 'less_than';
      value: string;
      condition_order: number;
      logic_operator?: 'AND' | 'OR';
    }>;
    simple_patterns?: Array<{
      pattern_type: 'merchant' | 'description' | 'amount' | 'account';
      pattern: string;
    }>;
  }) => {
    const response = await fetch(`${API_BASE_URL}/transactions/rules/preview`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${getAuthToken()}`, 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    if (!response.ok) throw new Error('Failed to preview rule matches');
    return response.json();
  },

  updateRule: async (id: number, data: { 
    rule_name: string; 
    rule_type: 'simple' | 'complex'; 
    category_id: number; 
    subcategory_id?: number | null; 
    confidence: number; 
    is_active: boolean;
    conditions?: Array<{
      field_type: 'merchant' | 'description' | 'amount' | 'account';
      operator: 'contains' | 'equals' | 'starts_with' | 'ends_with' | 'regex' | 'greater_than' | 'less_than';
      value: string;
      condition_order: number;
      logic_operator?: 'AND' | 'OR';
    }>;
    simple_patterns?: Array<{
      pattern_type: 'merchant' | 'description' | 'amount' | 'account';
      pattern: string;
    }>;
  }) => {
    const response = await fetch(`${API_BASE_URL}/transactions/rules/${id}`, {
      method: 'PUT',
      headers: { Authorization: `Bearer ${getAuthToken()}`, 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    if (!response.ok) throw new Error('Failed to update rule');
    return response.json();
  },

  deleteRule: async (id: number) => {
    const response = await fetch(`${API_BASE_URL}/transactions/rules/${id}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${getAuthToken()}` },
    });
    if (!response.ok) throw new Error('Failed to delete rule');
    return response.json();
  },

  toggleRule: async (id: number) => {
    const response = await fetch(`${API_BASE_URL}/transactions/rules/${id}/toggle`, {
      method: 'PATCH',
      headers: { Authorization: `Bearer ${getAuthToken()}` },
    });
    if (!response.ok) throw new Error('Failed to toggle rule');
    return response.json();
  },

  getTransactions: async (filters?: { cursorDate?: string; cursorId?: number; categoryId?: number; accountId?: number; startDate?: string; endDate?: string }) => {
    const params = new URLSearchParams();
    if (filters?.cursorDate) params.append('cursorDate', filters.cursorDate);
    if (filters?.cursorId != null) params.append('cursorId', String(filters.cursorId));
    if (filters?.categoryId) params.append('categoryId', String(filters.categoryId));
    if (filters?.accountId) params.append('accountId', String(filters.accountId));
    if (filters?.startDate) params.append('startDate', filters.startDate);
    if (filters?.endDate) params.append('endDate', filters.endDate);
    const qs = params.toString();
    return authRequest(`/transactions/transactions${qs ? `?${qs}` : ''}`);
  },

  /** Accounts that have at least one transaction (for Transactions table filter dropdown). */
  getTransactionFilterAccounts: async (): Promise<{ accounts: Array<{ id: number; name: string }> }> => {
    return authRequest('/transactions/transactions/filter-accounts');
  },

  deleteTransaction: async (transactionId: number) => {
    const response = await fetch(`${API_BASE_URL}/transactions/transactions/${transactionId}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${getAuthToken()}` },
    });
    if (!response.ok) throw new Error('Failed to delete transaction');
    return response.json();
  },
  
  // Import transactions CSV into a manual account
  importTransactionsCSV: async (params: { manual_account_id: number; file: File }) => {
    const form = new FormData();
    form.append('manual_account_id', String(params.manual_account_id));
    form.append('file', params.file);

    const token = getAuthToken();
    const response = await fetch(`${API_BASE_URL}/transactions/import`, {
      method: 'POST',
      headers: {
        ...(token && { Authorization: `Bearer ${token}` }),
      },
      body: form,
    });
    if (!response.ok) {
      const text = await response.text();
      try {
        const data = JSON.parse(text);
        throw new Error(data.error || 'Failed to import transactions');
      } catch {
        throw new Error('Failed to import transactions');
      }
    }
    return response.json();
  },

  importOverrides: async (params: { linked_account_id: number; transactions: Array<{ date: string; amount: number; description: string | null; merchant: string | null }> }) => {
    return authRequest('/transactions/transactions/import-overrides', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(params),
    });
  },

  // Import transactions PDF into a manual account
  importTransactionsPDF: async (params: {
    manual_account_id: number;
    file: File;
    preview_only?: boolean;
  }) => {
    const form = new FormData();
    form.append('manual_account_id', String(params.manual_account_id));
    form.append('file', params.file);
    if (params.preview_only) {
      form.append('preview_only', 'true');
    }

    const token = getAuthToken();
    const response = await fetch(`${API_BASE_URL}/transactions/import-pdf`, {
      method: 'POST',
      headers: {
        ...(token && { Authorization: `Bearer ${token}` }),
      },
      body: form,
    });
    if (!response.ok) {
      const text = await response.text();
      try {
        const data = JSON.parse(text);
        throw new Error(data.error || 'Failed to import PDF transactions');
      } catch {
        throw new Error('Failed to import PDF transactions');
      }
    }
    return response.json();
  },

  debugPDFParsing: async (data: {
    file: File;
  }) => {
    const form = new FormData();
    form.append('file', data.file);

    const token = getAuthToken();
    const response = await fetch(`${API_BASE_URL}/transactions/debug-pdf`, {
      method: 'POST',
      headers: {
        ...(token && { Authorization: `Bearer ${token}` }),
      },
      body: form,
    });
    if (!response.ok) {
      const text = await response.text();
      try {
        const data = JSON.parse(text);
        throw new Error(data.error || 'Failed to debug PDF');
      } catch {
        throw new Error('Failed to debug PDF');
      }
    }
    return response.json();
  },

  updateTransactionCategorization: async (transactionId: number, data: { category_id?: number; subcategory_id?: number; }) => {
    const response = await fetch(`${API_BASE_URL}/transactions/transactions/${transactionId}/categorization`, {
      method: 'PUT',
      headers: { Authorization: `Bearer ${getAuthToken()}`, 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    if (!response.ok) throw new Error('Failed to update transaction categorization');
    return response.json();
  },

  updateTransactionVerification: async (transactionId: number, data: { is_verified: boolean }) => {
    return authRequest(`/transactions/transactions/${transactionId}/verification`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  updateTransactionBusinessFlag: async (transactionId: number, data: { is_business_expense: boolean; business_category?: string }) => {
    return authRequest(`/transactions/transactions/${transactionId}/business-flag`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  updateTransactionNotes: async (transactionId: number, data: { notes: string }) => {
    return authRequest(`/transactions/transactions/${transactionId}/notes`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  updateTransaction: async (transactionId: number, data: { 
    amount?: number; 
    description?: string; 
    merchant_name?: string | null;
    date?: string;
  }) => {
    return authRequest(`/transactions/transactions/${transactionId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  getSubcategoryTransactions: async (subcategoryId: number) => {
    return authRequest(`/transactions/subcategories/${subcategoryId}/transactions`);
  },

  bulkReassignAndDeleteSubcategory: async (subcategoryId: number, reassignments: any[]) => {
    return authRequest(`/transactions/subcategories/${subcategoryId}/bulk-reassign-and-delete`, {
      method: 'POST',
      body: JSON.stringify({ reassignments }),
    });
  },

  getTransactionSplits: async (transactionId: number) => {
    return authRequest(`/transactions/transactions/${transactionId}/splits`);
  },

  createTransactionSplit: async (transactionId: number, data: {
    category_id: number;
    subcategory_id?: number | null;
    amount: number;
    notes?: string;
  }) => {
    return authRequest(`/transactions/transactions/${transactionId}/splits`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  updateTransactionSplit: async (splitId: number, data: {
    category_id?: number;
    subcategory_id?: number | null;
    amount?: number;
    notes?: string;
  }) => {
    return authRequest(`/transactions/transactions/splits/${splitId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },

  deleteTransactionSplit: async (splitId: number) => {
    return authRequest(`/transactions/transactions/splits/${splitId}`, {
      method: 'DELETE',
    });
  },

  // DEPRECATED: updateTransactionReimbursement - Use insurance reimbursement linking instead
  // Removed: Expense-side reimbursement form has been removed. Use the 🏥 button on income transactions
  // to link reimbursements to expenses via the insurance_reimbursements table.
  
  autoCategorizeTransactions: async () => {
    const response = await fetch(`${API_BASE_URL}/transactions/transactions/auto-categorize`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${getAuthToken()}` },
    });
    if (!response.ok) throw new Error('Failed to auto-categorize transactions');
    return response.json();
  },
  
  getTransactionYears: async (): Promise<{ years: number[] }> => {
    const response = await fetch(`${API_BASE_URL}/transactions/transaction-years`, {
      headers: { Authorization: `Bearer ${getAuthToken()}` },
    });
    if (!response.ok) throw new Error('Failed to get transaction years');
    return response.json();
  },

  getCashFlowSummary: async (filters?: { startDate?: string; endDate?: string }) => {
    const params = new URLSearchParams();
    if (filters?.startDate) params.append('startDate', filters.startDate);
    if (filters?.endDate) params.append('endDate', filters.endDate);
    
    const response = await fetch(`${API_BASE_URL}/transactions/cash-flow-summary?${params}`, {
      headers: { Authorization: `Bearer ${getAuthToken()}` },
    });
    if (!response.ok) throw new Error('Failed to get cash flow summary');
    return response.json();
  },
  
  getCategoryBreakdown: async (filters?: { startDate?: string; endDate?: string }) => {
    const params = new URLSearchParams();
    if (filters?.startDate) params.append('startDate', filters.startDate);
    if (filters?.endDate) params.append('endDate', filters.endDate);
    
    const response = await fetch(`${API_BASE_URL}/transactions/category-breakdown?${params}`, {
      headers: { Authorization: `Bearer ${getAuthToken()}` },
    });
    if (!response.ok) throw new Error('Failed to get category breakdown');
    return response.json();
  },

  // Insurance reimbursement methods
  markInsuranceEligible: async (transactionId: number, isReimbursable: boolean) => {
    return authRequest(`/transactions/transactions/${transactionId}/insurance-eligible`, {
      method: 'PUT',
      body: JSON.stringify({ is_reimbursable: isReimbursable }),
    });
  },

  createInsuranceReimbursement: async (data: {
    income_transaction_id: number;
    expense_transaction_id: number;
    covered_amount: number;
    notes?: string;
  }) => {
    return authRequest('/transactions/insurance-reimbursements', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  getInsuranceReimbursements: async (transactionId: number) => {
    return authRequest(`/transactions/transactions/${transactionId}/insurance-reimbursements`);
  },

  deleteInsuranceReimbursement: async (reimbursementId: number) => {
    return authRequest(`/transactions/insurance-reimbursements/${reimbursementId}`, {
      method: 'DELETE',
    });
  },

  getPotentialExpenses: async (incomeTransactionId: number) => {
    return authRequest(`/transactions/transactions/${incomeTransactionId}/potential-expenses`);
  },

  getIncomeSourcesBreakdown: async (filters?: { startDate?: string; endDate?: string }) => {
    const params = new URLSearchParams();
    if (filters?.startDate) params.append('startDate', filters.startDate);
    if (filters?.endDate) params.append('endDate', filters.endDate);
    
    const response = await fetch(`${API_BASE_URL}/transactions/income-sources-breakdown?${params}`, {
      headers: { Authorization: `Bearer ${getAuthToken()}` },
    });
    
    if (!response.ok) {
      throw new Error('Failed to fetch income sources breakdown');
    }
    
    return response.json();
  },

  getExpensesDrillDown: async (filters?: { startDate?: string; endDate?: string }) => {
    const params = new URLSearchParams();
    if (filters?.startDate) params.append('startDate', filters.startDate);
    if (filters?.endDate) params.append('endDate', filters.endDate);
    
    const response = await fetch(`${API_BASE_URL}/transactions/expenses-drill-down?${params}`, {
      headers: { Authorization: `Bearer ${getAuthToken()}` },
    });
    
    if (!response.ok) {
      throw new Error('Failed to fetch expenses drill down');
    }
    
    return response.json();
  },

  findTransferMatches: async (transactionId: number) => {
    const response = await fetch(`${API_BASE_URL}/transactions/${transactionId}/transfer-matches`, {
      headers: { Authorization: `Bearer ${getAuthToken()}` },
    });
    if (!response.ok) throw new Error('Failed to find transfer matches');
    return response.json();
  },

  confirmTransfer: async (transactionId: number, transferAccountId: number, transferTransactionId: number) => {
    const response = await fetch(`${API_BASE_URL}/transactions/${transactionId}/confirm-transfer`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${getAuthToken()}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ transfer_account_id: transferAccountId, transfer_transaction_id: transferTransactionId }),
    });
    if (!response.ok) throw new Error('Failed to confirm transfer');
    return response.json();
  },

  getTransferCounterpart: async (transactionId: number) => {
    const response = await fetch(`${API_BASE_URL}/transactions/${transactionId}/transfer-counterpart`, {
      headers: { Authorization: `Bearer ${getAuthToken()}` },
    });
    if (!response.ok) throw new Error('Failed to fetch transfer counterpart');
    return response.json();
  },

  markAsRefund: async (transactionId: number, data: {
    category_id: number;
    subcategory_id?: number | null;
  }) => {
    const response = await fetch(`${API_BASE_URL}/transactions/${transactionId}/mark-as-refund`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${getAuthToken()}`, 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    if (!response.ok) throw new Error('Failed to mark transaction as refund');
    return response.json();
  },

  clearRefund: async (transactionId: number) => {
    const response = await fetch(`${API_BASE_URL}/transactions/${transactionId}/clear-refund`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${getAuthToken()}`, 'Content-Type': 'application/json' },
    });
    if (!response.ok) throw new Error('Failed to clear refund relationship');
    return response.json();
  },

  resolveCategorizationConflicts: async (data: {
    transactionIds: number[];
    action: 'apply_rules' | 'categorize_manually' | 'leave_uncategorized' | 'mark_as_refunds';
    categoryId?: number;
    subcategoryId?: number;
    conflicts?: any[];
  }) => {
    const response = await fetch(`${API_BASE_URL}/transactions/transactions/resolve-conflicts`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${getAuthToken()}`, 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    if (!response.ok) throw new Error('Failed to resolve categorization conflicts');
    return response.json();
  },

};

// Plaid API
export const plaidAPI = {
  createLinkToken: async () => {
    const response = await authRequest('/plaid/link-token', {
      method: 'POST'
    });
    return response;
  },

  exchangeToken: async (publicToken: string) => {
    const response = await authRequest('/plaid/exchange-token', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ public_token: publicToken }),
    });
    return response;
  },

  getLinkedAccounts: async () => {
    const response = await authRequest('/plaid/linked-accounts');
    return response;
  },

  importSelectedAccounts: async (data: { connectionId: number; selectedAccountIds: string[] }) => {
    const response = await authRequest('/plaid/import-accounts', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });
    return response;
  },

  getAccountMatchingSuggestions: async () => {
    const response = await authRequest('/plaid/account-matching-suggestions');
    return response;
  },

  disconnectAccount: async (linkedAccountId: number) => {
    const response = await authRequest(`/plaid/disconnect-account/${linkedAccountId}`, {
      method: 'DELETE',
    });
    return response;
  },

  unlinkPlaidToManual: async (data: { linkedAccountId: number; manualAccountId: number | null }) => {
    const response = await authRequest('/plaid/unlink-account', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });
    return response;
  },

  getAccountTransactions: async (linkedAccountId: number, startDate?: string, endDate?: string) => {
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    
    const response = await authRequest(`/plaid/account-transactions/${linkedAccountId}?${params}`);
    return response;
  },

  syncAllTransactions: async () => {
    const response = await authRequest('/plaid/sync-transactions', {
      method: 'POST',
    });
    return response;
  },
};

// Business Accounting API
export const businessAccountingAPI = {
  getBusinessAccountingCategories: async () => {
    return authRequest('/business-accounting/categories');
  },

  getCategoryMappings: async () => {
    return authRequest('/business-accounting/mappings');
  },

  getCategoriesWithMappings: async () => {
    return authRequest('/business-accounting/categories-with-mappings');
  },

  upsertCategoryMapping: async (data: {
    category_id: number;
    subcategory_id?: number | null;
    business_accounting_category_id: number;
  }) => {
    return authRequest('/business-accounting/mappings', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  deleteCategoryMapping: async (data: {
    category_id: number;
    subcategory_id?: number | null;
  }) => {
    return authRequest('/business-accounting/mappings', {
      method: 'DELETE',
      body: JSON.stringify(data),
    });
  },

  validateMappings: async () => {
    return authRequest('/business-accounting/validate-mappings');
  },

  getCategorySettings: async (year?: number) => {
    const url = year ? `/business-accounting/category-settings?year=${year}` : '/business-accounting/category-settings';
    return authRequest(url);
  },

  updateCategorySettings: async (data: {
    business_accounting_category_id: number;
    business_percentage?: number;
    tax_type: string;
    manual_total?: number;
  }) => {
    return authRequest('/business-accounting/category-settings', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },
};

// Export the main API object
export default {
  auth: authAPI,
  accounts: accountsAPI,
  legacy: legacyAPI,
  transactions: transactionAPI,
  plaid: plaidAPI,
  businessAccounting: businessAccountingAPI,
}; 